import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAction, useResource } from '../lib/useApi.js';
import { useAuth } from '../lib/auth.js';
import type {
  Deployment,
  DirectoryGroup,
  DirectoryStatus,
  PreflightResponse,
  ProviderInfo,
  TargetType,
  TemplateDetail,
  TemplateSummary,
} from '../lib/types.js';
import {
  Banner,
  Card,
  Check,
  Empty,
  ErrorState,
  Field,
  Loading,
  Pill,
  StatusPill,
  When,
  useToast,
} from '../components/ui.js';

/**
 * New deployment.
 *
 * Structured as choose → preflight → confirm rather than a single form, because
 * the interesting information only exists after the target has been resolved:
 * how many people it hits, whether the provider can do what is being asked, and
 * what it cannot do. Nothing runs until that has been shown.
 */
export function DeployPage(): JSX.Element {
  const navigate = useNavigate();
  const { me } = useAuth();
  const toast = useToast();

  const templates = useResource<TemplateSummary[]>('/api/templates');
  const providers = useResource<{ providers: ProviderInfo[]; globalDryRun: boolean }>(
    '/api/deployments/providers',
  );
  const groups = useResource<{ groups: DirectoryGroup[] }>('/api/directory/groups');
  const directory = useResource<DirectoryStatus>('/api/directory/status');
  const facets = useResource<{ departments: Array<{ value: string | null; count: number }> }>(
    '/api/directory/facets',
  );

  const [templateId, setTemplateId] = useState('');
  const [versionId, setVersionId] = useState('');
  const [providerKey, setProviderKey] = useState('');
  const [targetType, setTargetType] = useState<TargetType>('USER');
  const [targetValue, setTargetValue] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [scheduled, setScheduled] = useState(false);
  const [scheduledFor, setScheduledFor] = useState('');
  const [temporary, setTemporary] = useState(false);
  const [expiresAt, setExpiresAt] = useState('');
  const [preflight, setPreflight] = useState<PreflightResponse | null>(null);

  const template = useResource<TemplateDetail>(templateId ? `/api/templates/${templateId}` : null);

  const canDeploy = Boolean(me?.capabilities.canDeploy);
  const canSchedule = Boolean(me?.capabilities.canSchedule);

  const publishedTemplates = useMemo(
    () => (templates.data ?? []).filter((t) => t.publishedVersion),
    [templates.data],
  );

  useEffect(() => {
    if (!providerKey && providers.data) {
      const preferred = providers.data.providers.find((p) => p.isDefault);
      setProviderKey(preferred?.key ?? providers.data.providers[0]?.key ?? '');
    }
  }, [providerKey, providers.data]);

  useEffect(() => {
    if (!templateId && publishedTemplates[0]) setTemplateId(publishedTemplates[0].id);
  }, [publishedTemplates, templateId]);

  useEffect(() => {
    // Default to the live version whenever the template changes.
    if (template.data) setVersionId(template.data.publishedVersionId ?? '');
  }, [template.data]);

  // Any change to the request invalidates a previous preflight result.
  useEffect(() => {
    setPreflight(null);
  }, [templateId, versionId, providerKey, targetType, targetValue, dryRun, scheduledFor, expiresAt]);

  const provider = providers.data?.providers.find((p) => p.key === providerKey) ?? null;

  const targetReady = targetType === 'ALL_USERS' || targetValue.trim().length > 0;

  /**
   * A ticked box with an empty date used to be silently ignored -- the request
   * simply omitted the field and the push ran immediately, or ran forever. Ask
   * for the date instead of quietly doing something other than what the form
   * says.
   */
  const timingProblem =
    scheduled && canSchedule && !scheduledFor
      ? 'Set a start date, or untick "Schedule for later".'
      : temporary && canSchedule && !expiresAt
        ? 'Set an end date, or untick "Temporary".'
        : null;
  const requestBody = () => ({
    templateId,
    ...(versionId ? { versionId } : {}),
    providerKey,
    target:
      targetType === 'ALL_USERS'
        ? { type: 'ALL_USERS' as const }
        : { type: targetType, value: targetValue.trim() },
    dryRun,
  });

  const runPreflight = useAction(async () => {
    const result = await api.post<PreflightResponse>('/api/deployments/preflight', requestBody());
    setPreflight(result);
  });

  const start = useAction(async () => {
    const body = {
      ...requestBody(),
      ...(scheduled && scheduledFor ? { scheduledFor: new Date(scheduledFor).toISOString() } : {}),
      ...(temporary && expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
      ...(targetType === 'USER' ? { kind: 'TEST' as const } : {}),
    };
    const result = await api.post<{ deployment: Deployment; message: string }>(
      '/api/deployments',
      body,
    );
    toast.success(scheduled ? 'Deployment scheduled' : 'Deployment started', result.message);
    navigate(`/deployments/${result.deployment.id}`);
  });

  if (!canDeploy) {
    return (
      <>
        <header className="page-head">
          <div>
            <h1>Deploy</h1>
          </div>
        </header>
        <div className="page-body">
          <Card>
            <Empty title="Deployment is an IT function">
              Your role ({me?.role.toLowerCase()}) can design, preview and review templates, and can
              see every deployment outcome, but cannot push to Exchange. Ask IT to run the push once a
              version is published.
            </Empty>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <header className="page-head">
        <div>
          <h1>New deployment</h1>
          <p className="sub">
            Push a published version to a target population. Every push is recorded per user, and can
            be retried or rolled back afterwards.
          </p>
        </div>
      </header>

      <div className="page-body">
        {providers.data?.globalDryRun && (
          <Banner tone="warn">
            <strong>The server is in global dry-run mode</strong>
            <div className="tiny">
              <code>DEPLOY_DRY_RUN=true</code> — deployments report what they would do without
              changing the tenant. Turn it off in the app settings once a single-user test looks right.
            </div>
          </Banner>
        )}

        {directory.data?.isStale && (
          <Banner tone="warn">
            <strong>The directory snapshot is stale</strong>
            <div className="tiny">
              Last synced{' '}
              {directory.data.lastSuccessfulSyncAt ? (
                <When iso={directory.data.lastSuccessfulSyncAt} />
              ) : (
                'never'
              )}
              . Targets resolve against the snapshot, so leavers may still be included and joiners
              missed. <Link to="/admin">Run a sync</Link> first.
            </div>
          </Banner>
        )}

        {publishedTemplates.length === 0 && (
          <Card>
            <Empty title="Nothing is published yet">
              A version must be approved and published before it can be deployed.{' '}
              <Link to="/templates">Open templates</Link> to see what is in review.
            </Empty>
          </Card>
        )}

        {publishedTemplates.length > 0 && (
          <div className="grid cols-2">
            <Card title="What to send">
              <Field label="Template">
                <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                  {publishedTemplates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="Version"
                hint="Only a published version can be pushed to a group. A single-user test may use any version."
              >
                <select value={versionId} onChange={(e) => setVersionId(e.target.value)}>
                  {(template.data?.versions ?? []).map((v) => (
                    <option key={v.id} value={v.id}>
                      v{v.version} — {v.status.toLowerCase()}
                      {v.id === template.data?.publishedVersionId ? ' (live)' : ''}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Delivery mechanism">
                <select value={providerKey} onChange={(e) => setProviderKey(e.target.value)}>
                  {(providers.data?.providers ?? []).map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.displayName}
                      {p.isDefault ? ' (recommended)' : ''}
                    </option>
                  ))}
                </select>
              </Field>

              {provider && <ProviderCard provider={provider} />}
            </Card>

            <Card title="Who receives it">
              <Field label="Target">
                <select
                  value={targetType}
                  onChange={(e) => {
                    setTargetType(e.target.value as TargetType);
                    setTargetValue('');
                  }}
                >
                  <option value="USER">One person (test)</option>
                  <option value="GROUP">A security group</option>
                  <option value="ATTRIBUTE">An attribute value</option>
                  <option value="OU">An on-premises OU</option>
                  <option value="ALL_USERS">Everyone</option>
                </select>
              </Field>

              {targetType === 'USER' && (
                <Field
                  label="User principal name"
                  hint="Recorded as a TEST deployment, which is the only kind allowed to use an unpublished version."
                >
                  <input
                    value={targetValue}
                    className="mono"
                    placeholder="alex.mercer@contoso.com"
                    onChange={(e) => setTargetValue(e.target.value)}
                  />
                </Field>
              )}

              {targetType === 'GROUP' && (
                <Field label="Group">
                  <select value={targetValue} onChange={(e) => setTargetValue(e.target.value)}>
                    <option value="">Choose a group…</option>
                    {(groups.data?.groups ?? []).map((g) => (
                      <option key={g.entraObjectId} value={g.entraObjectId}>
                        {g.displayName} ({g.memberCount})
                      </option>
                    ))}
                  </select>
                </Field>
              )}

              {targetType === 'ATTRIBUTE' && (
                <Field label="Attribute expression" hint="For example department=Marketing.">
                  <input
                    value={targetValue}
                    className="mono"
                    placeholder="department=Marketing"
                    onChange={(e) => setTargetValue(e.target.value)}
                    list="department-values"
                  />
                  <datalist id="department-values">
                    {(facets.data?.departments ?? []).map((d) => (
                      <option key={d.value ?? ''} value={`department=${d.value ?? ''}`}>
                        {d.count} user(s)
                      </option>
                    ))}
                  </datalist>
                </Field>
              )}

              {targetType === 'OU' && (
                <Field label="Distinguished-name suffix">
                  <input
                    value={targetValue}
                    className="mono"
                    placeholder="OU=Marketing,DC=contoso,DC=com"
                    onChange={(e) => setTargetValue(e.target.value)}
                  />
                </Field>
              )}

              {targetType === 'ALL_USERS' && (
                <Banner tone="warn">
                  Every enabled mailbox in the directory snapshot (
                  {directory.data?.userCount ?? '…'} users). Run a single-user test first.
                </Banner>
              )}

              <hr style={{ border: 0, borderTop: '1px solid var(--border)', margin: '14px 0' }} />

              <Check
                label="Dry run"
                checked={dryRun}
                onChange={setDryRun}
                hint="Reports exactly what would happen, including the commands that would run, without changing anything."
              />

              <Check
                label="Schedule for later"
                checked={scheduled}
                onChange={setScheduled}
                hint={
                  canSchedule
                    ? 'For a rebrand launch, arm it the night before.'
                    : 'Your role cannot schedule deployments.'
                }
              />
              {scheduled && canSchedule && (
                <Field label="Start at" hint="Server local time. The scheduler polls every 30 seconds.">
                  <input
                    type="datetime-local"
                    value={scheduledFor}
                    onChange={(e) => setScheduledFor(e.target.value)}
                  />
                </Field>
              )}

              <Check
                label="Temporary — take it down again on a date"
                checked={temporary}
                onChange={setTemporary}
                hint={
                  canSchedule
                    ? 'For a marketing banner. At the end date the scheduler rolls this deployment back, restoring exactly what was live before it ran. No second review cycle.'
                    : 'Your role cannot set an end date.'
                }
              />
              {temporary && canSchedule && (
                <Field
                  label="End at"
                  hint="Server local time. It must be after the start. If the rollback fails it is not retried — check History."
                >
                  <input
                    type="datetime-local"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                  />
                </Field>
              )}

              <div className="row mt">
                <button
                  className="primary"
                  disabled={!targetReady || !templateId || Boolean(timingProblem) || runPreflight.pending}
                  onClick={() => void runPreflight.run()}
                >
                  {runPreflight.pending ? 'Checking…' : 'Run preflight'}
                </button>
                {!targetReady && <span className="tiny muted">Choose a target first.</span>}
                {targetReady && timingProblem && (
                  <span className="tiny muted">{timingProblem}</span>
                )}
              </div>
            </Card>
          </div>
        )}

        {runPreflight.error && <ErrorState error={runPreflight.error} />}
        {start.error && <ErrorState error={start.error} />}

        {runPreflight.pending && (
          <Card className="mt">
            <Loading rows={3} label="Resolving target" />
          </Card>
        )}

        {preflight && (
          <div className="mt">
            <PreflightCard
              preflight={preflight}
              scheduled={scheduled && canSchedule ? scheduledFor : null}
              pending={start.pending}
              onStart={() => void start.run()}
            />
          </div>
        )}
      </div>
    </>
  );
}

function ProviderCard({ provider }: { provider: ProviderInfo }): JSX.Element {
  const c = provider.capabilities;
  return (
    <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: 12 }}>
      <p className="tiny" style={{ marginBottom: 8 }}>
        {provider.description}
      </p>
      <div className="row wrap mb">
        <Pill tone={c.unattended ? 'ok' : 'warn'}>
          {c.unattended ? 'unattended' : 'needs per-user consent'}
        </Pill>
        <Pill tone={c.visibleInCompose ? 'ok' : 'warn'}>
          {c.visibleInCompose ? 'visible while composing' : 'not visible while composing'}
        </Pill>
        <Pill tone={c.coversAllClients ? 'ok' : 'warn'}>
          {c.coversAllClients ? 'all clients' : 'limited client coverage'}
        </Pill>
        <Pill tone={c.supportsRollback ? 'ok' : 'outline'}>
          {c.supportsRollback ? 'rollback supported' : 'no rollback'}
        </Pill>
      </div>
      <div className="field-label">Trade-offs</div>
      <ul className="tiny muted" style={{ margin: 0, paddingLeft: 18 }}>
        {c.caveats.map((caveat, i) => (
          <li key={i}>{caveat}</li>
        ))}
      </ul>
    </div>
  );
}

function PreflightCard({
  preflight,
  scheduled,
  pending,
  onStart,
}: {
  preflight: PreflightResponse;
  scheduled: string | null;
  pending: boolean;
  onStart: () => void;
}): JSX.Element {
  const errors = preflight.preflight.issues.filter((i) => i.severity === 'error');
  const warnings = preflight.preflight.issues.filter((i) => i.severity === 'warning');

  return (
    <Card
      title="Preflight"
      actions={
        <button
          className="primary"
          disabled={!preflight.canProceed || pending}
          onClick={onStart}
          title={!preflight.canProceed ? 'Resolve the blocking issues first' : undefined}
        >
          {pending
            ? 'Starting…'
            : scheduled
              ? `Schedule for ${new Date(scheduled).toLocaleString()}`
              : preflight.dryRun
                ? 'Run dry run'
                : `Push to ${preflight.target.userCount} user(s)`}
        </button>
      }
    >
      <div className="grid cols-3 mb">
        <div>
          <div className="field-label">Target</div>
          <div className="cell-strong">{preflight.target.label}</div>
        </div>
        <div>
          <div className="field-label">Version</div>
          <div className="row">
            <span className="cell-strong">v{preflight.version.version}</span>
            <StatusPill status={preflight.version.status} />
          </div>
        </div>
        <div>
          <div className="field-label">Mode</div>
          <div>
            {preflight.dryRun ? (
              <Pill tone="accent">dry run</Pill>
            ) : (
              <Pill tone="danger">live — will change the tenant</Pill>
            )}
          </div>
        </div>
      </div>

      {errors.length > 0 && (
        <Banner tone="danger">
          <strong>Cannot proceed</strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {errors.map((issue, i) => (
              <li key={i}>
                {issue.message}
                {issue.hint && <div className="tiny muted">{issue.hint}</div>}
              </li>
            ))}
          </ul>
        </Banner>
      )}

      {warnings.length > 0 && (
        <Banner tone="warn">
          <strong>Worth knowing before you run it</strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {warnings.map((issue, i) => (
              <li key={i}>
                {issue.message}
                {issue.hint && <div className="tiny muted">{issue.hint}</div>}
              </li>
            ))}
          </ul>
        </Banner>
      )}

      {preflight.sampleUsers.length > 0 && (
        <>
          <div className="field-label">
            First {preflight.sampleUsers.length} of {preflight.target.userCount} recipients
          </div>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>UPN</th>
                  <th>Department</th>
                  <th>Job title</th>
                </tr>
              </thead>
              <tbody>
                {preflight.sampleUsers.map((u) => (
                  <tr key={u.upn}>
                    <td>{u.displayName ?? '—'}</td>
                    <td className="mono tiny">{u.upn}</td>
                    <td className="tiny">{u.department ?? <span className="muted">—</span>}</td>
                    <td className="tiny">{u.jobTitle ?? <span className="muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}
